import { FormData } from "../../../../models/form/form";
import { getCurrentFinancialYear } from "../../../../utilities/dates";
import { InitiativePopUp } from "../initiative_popup";
import { ManagePastures_Post_V3_4_DTO } from "../../../../models/reduction_plans_models";
import { Report } from "../../../../models/property_models";
import { SliderFormField } from "../../../../models/form/form_fields/basic_form_fields";
import { getYearField, getDefaultReductionPlanYearOptions, addExistingReductionPlanYearsToOptions } from "../initiative_helpers";
import { FieldSize } from "../../../../models/form/form_fields/form_field";
import { calculateSumOfArray } from "@/utilities/maths";

interface PastureFormData {
    urea: number;
    ureaTargetYear: number;
    lime: number;
    limeTargetYear: number;
    singleSuperphosphate: number;
    singleSuperphosphateTargetYear: number;
    pesticideHerbicide: number;
    pesticideHerbicideTargetYear: number;
}

export default function ManagePasturePost_V3_4_Popup(props: {
    initialData?: ManagePastures_Post_V3_4_DTO;
    compareReport?: Report;
    onSubmit: (data: ManagePastures_Post_V3_4_DTO) => Promise<void>
}) {

    const yearOptions = getDefaultReductionPlanYearOptions()
    const existingReductionPlanYears = [
        props.initialData?.lime?.targetYear,
        props.initialData?.pesticideHerbicide?.targetYear,
        props.initialData?.singleSuperphosphate?.targetYear,
        props.initialData?.urea?.targetYear
    ].filter(v => v !== undefined) as number[]
    addExistingReductionPlanYearsToOptions(existingReductionPlanYears, yearOptions)

    const prevUrea = props.compareReport?.pastureInformationPostV3_4 ? calculateSumOfArray([
      props.compareReport?.pastureInformationPostV3_4?.ureaDrylandCrops,
      props.compareReport?.pastureInformationPostV3_4?.ureaDrylandPasture,
      props.compareReport?.pastureInformationPostV3_4?.ureaIrrigatedCrops,
      props.compareReport?.pastureInformationPostV3_4?.ureaIrrigatedPasture
    ]) : 0

    const prevPesticideHerbicide = props.compareReport?.pastureInformationPostV3_4 ? calculateSumOfArray([
      props.compareReport?.pastureInformationPostV3_4?.herbicide,
      props.compareReport?.pastureInformationPostV3_4?.herbicidePesticide
    ]) : 0

    const initialData: ManagePastures_Post_V3_4_DTO = props.initialData ?? {
        urea: {
            targetValue: prevUrea,
            targetYear: getCurrentFinancialYear()
        },
        lime: {
            targetValue: props.compareReport?.pastureInformationPostV3_4?.lime ?? 0,
            targetYear: getCurrentFinancialYear(),
        },
        singleSuperphosphate: {
            targetValue: props.compareReport?.pastureInformationPostV3_4?.singleSuperphosphate ?? 0,
            targetYear: getCurrentFinancialYear()
        },
        pesticideHerbicide: {
            targetValue: prevPesticideHerbicide,
            targetYear: getCurrentFinancialYear()
        }
    }

    const formData: FormData<PastureFormData, ManagePastures_Post_V3_4_DTO> = {
        fields: {
            urea: new SliderFormField({
                required: true,
                sliderProps: {
                    defaultRange: { min: 0, max: 1000 },
                    prevReportValue: prevUrea,
                    initialValue: initialData.urea?.targetValue ?? 0,
                    unitLabel: "tonnes",
                    label: "Urea (tonnes/year)",
                },
            }),
            ureaTargetYear: getYearField('Year of completion', FieldSize.Full, yearOptions),
            lime: new SliderFormField({
                required: true,
                sliderProps: {
                    defaultRange: { min: 0, max: 1000 },
                    prevReportValue: props.compareReport?.pastureInformationPostV3_4?.lime,
                    initialValue: initialData?.lime?.targetValue ?? 0,
                    label: "Lime (tonnes/year)",
                    unitLabel: "tonnes",
                },
            }),
            limeTargetYear: getYearField('Year of completion', FieldSize.Full, yearOptions),
            singleSuperphosphate: new SliderFormField({
                required: true,
                sliderProps: {
                    defaultRange: { min: 0, max: 1000 },
                    prevReportValue: props.compareReport?.pastureInformationPostV3_4?.singleSuperphosphate,
                    initialValue: initialData?.singleSuperphosphate?.targetValue ?? 0,
                    label: "Single Superphosphate (tonnes/year)",
                    unitLabel: "tonnes",
                },
            }),
            singleSuperphosphateTargetYear: getYearField('Year of completion', FieldSize.Full, yearOptions),
            pesticideHerbicide: new SliderFormField({
                required: true,
                sliderProps: {
                    defaultRange: { min: 0, max: 1000 },
                    prevReportValue: prevPesticideHerbicide,
                    initialValue: initialData?.pesticideHerbicide?.targetValue ?? 0,
                    label: "Pesticide Purchased (litres/year)",
                    unitLabel: "litres",
                },
            }),
            pesticideHerbicideTargetYear: getYearField('Year of completion', FieldSize.Full, yearOptions),
        },
        transformer: {
            in: (value) => ({
                urea: value.urea?.targetValue,
                ureaTargetYear: value.urea?.targetYear,
                lime: value.lime?.targetValue,
                limeTargetYear: value.lime?.targetYear,
                singleSuperphosphate: value.singleSuperphosphate?.targetValue,
                singleSuperphosphateTargetYear: value.singleSuperphosphate?.targetYear,
                pesticideHerbicide: value.pesticideHerbicide?.targetValue,
                pesticideHerbicideTargetYear: value.pesticideHerbicide?.targetYear,
            }),
            out: (value) => ({
                ...(value.ureaTargetYear) ? {
                    urea: {
                        targetValue: value.urea ?? 0,
                        targetYear: value.ureaTargetYear
                    }
                } : {},
                ...(value.limeTargetYear) ? {
                    lime: {
                        targetValue: value.lime ?? 0,
                        targetYear: value.limeTargetYear
                    }
                } : {},
                ...(value.singleSuperphosphateTargetYear) ? {
                    singleSuperphosphate: {
                        targetValue: value.singleSuperphosphate ?? 0,
                        targetYear: value.singleSuperphosphateTargetYear
                    }
                } : {},
                ...(value.pesticideHerbicideTargetYear) ? {
                    pesticideHerbicide: {
                        targetValue: value.pesticideHerbicide ?? 0,
                        targetYear: value.pesticideHerbicideTargetYear
                    }
                } : {}
            })
        }
    };
    return (
        <InitiativePopUp
            formData={formData}
            initialFormValues={initialData}
            onSubmit={(state) => props.onSubmit(state)}
        />
    );
}
